import React from 'react';
import dayjs from 'dayjs';
import { ArrowLeftIcon } from 'components/common/icons';
import { Button, Col, Collapse, Divider, Dropdown, Form, Row, Typography } from 'components';
import { PaymentTerms } from './paymentTerms';
import styles from './styles.module.scss';
import { Delivery } from './delivery';
import { useEdit } from './useEdit';
import { General } from './general';
import { DownOutlined } from '@ant-design/icons';
import { CommercialOfferItemsTable } from './CommercialOfferItems';
import { InputNumber, Tag } from 'antd';
import { preventNonNumericValue, toNumberOrMinusOne } from 'utils/helpers';
import { AddressAddEditModal } from 'feature/clientTabs/shipping/shippingAddresses/addEdit';
import { ADMIN_ROUTES } from 'constants/routes';
import { Link } from 'react-router-dom';
import { SUPPLIER_OFFER_SOURCE } from 'models/SupplierOffer';

const { Title, Text } = Typography;

export const CommercialOfferUpdate = React.memo(() => {
  const {
    form,
    navigateToCards,
    handleMenuClick,
    deliveryDetails,
    paymentTerms,
    onFinish,
    commercialOfferId,
    handleFormItemBlur,
    brands,
    addModalState,
    commercialOfferDetails,
    onAddModalStateChange,
    formattedShippingData,
    isFinalStatus,
    availableActions,
    isLoading,
  } = useEdit();
  return (
    <>
      <Row gutter={[0, 52]} style={{ padding: '20px 28px 20px 60px' }} className={'margin-t-20'}>
        <Col span={24}>
          <Form form={form} layout="vertical" requiredMark={false}>
            <Row gutter={[0, 24]}>
              <Col span={24}>
                <Row align={'middle'} justify={'center'}>
                  <Col className={styles.back_btn}>
                    <Button
                      title={'Click to go back'}
                      onClick={() => history.back()}
                      type={'link'}
                      icon={<ArrowLeftIcon />}
                    />
                  </Col>
                  <Title level={2} className={'reset-title font-weight-800 center'}>
                    Update Commercial Offer
                  </Title>
                </Row>
              </Col>
              <Col span={24}>
                <Row gutter={28}>
                  <Col span={12}>
                    <Row gutter={[0, 16]}>
                      <Col span={24}>
                        <General onFinish={onFinish} isFinalStatus={isFinalStatus} />
                      </Col>
                      <Col span={24}>
                        <Delivery
                          onFinish={onFinish}
                          clientShippingAddress={formattedShippingData}
                          form={form}
                          deliveryDetails={deliveryDetails}
                          onAddModalStateChange={onAddModalStateChange}
                          isFinalStatus={isFinalStatus}
                          isLoading={isLoading}
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col span={12}>
                    <Row gutter={[0, 16]}>
                      <Col span={24}>
                        <PaymentTerms
                          form={form}
                          paymentTerms={paymentTerms}
                          onFinish={onFinish}
                          isFinalStatus={isFinalStatus}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        </Col>
        <Col span={24}>
          {brands?.map((_brand: any, index) => {
            return (
              <Collapse
                key={_brand.purchaseRequestId}
                className={styles.collapse}
                accordion
                expandIconPosition="end"
                items={[
                  {
                    key: 'supplier-email',
                    label: (
                      <Row justify={'space-between'} align={'middle'}>
                        <Col>
                          <Row>
                            <Col>
                              <Link
                                to={`${ADMIN_ROUTES.brand.view}/${_brand.brandId}/management`}
                                onClick={(e) => e.stopPropagation()} // prevent collapse toggle
                                target={'_blank'}
                              >
                                <Text underline className={'font-size-18 font-weight-800'}>
                                  {_brand.brandName}
                                </Text>
                              </Link>
                            </Col>
                          </Row>
                        </Col>
                        <Col>
                          <Row align={'middle'} gutter={16}>
                            <Col>
                              <Link
                                to={`${ADMIN_ROUTES.purchaseRequest.view}/${_brand.purchaseRequestId}/${ADMIN_ROUTES.purchaseRequest.supplierOfferUpdate}`
                                  .replace(':source', SUPPLIER_OFFER_SOURCE.PR_DETAILS)
                                  .replace(':supplierOfferId', _brand.supplierOfferId)}
                                onClick={(e) => e.stopPropagation()} // prevent collapse toggle
                                target={'_blank'}
                              >
                                <Text underline className={'font-weight-500'} style={{ color: '#05796B' }}>
                                  Supplier Offer
                                </Text>
                              </Link>
                            </Col>
                            <Col>
                              <Text>
                                Valid until:
                                {_brand.supplierOfferValidityDate
                                  ? dayjs(_brand.supplierOfferValidityDate).format('YYYY-MM-DD')
                                  : ''}
                              </Text>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    ),
                    children: (
                      <Row gutter={[0, 32]}>
                        <Col span={14}>
                          <Form form={form} layout="vertical" requiredMark={false}>
                            <Row gutter={12} align={'middle'}>
                              <Col span={6}>
                                <Form.Item name={['brands', index, 'leadTimeMin']} label={'Lead Time'}>
                                  <InputNumber<number>
                                    disabled={isFinalStatus}
                                    controls={false}
                                    size={'large'}
                                    onBlur={(e) =>
                                      handleFormItemBlur(
                                        'leadTimeMin',
                                        toNumberOrMinusOne(e.target.value),
                                        _brand.purchaseRequestId
                                      )
                                    }
                                    onBeforeInput={(event: any) => preventNonNumericValue(event)}
                                    style={{ width: '100%' }}
                                    placeholder={'Min'}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={6}>
                                <Form.Item
                                  name={['brands', index, 'leadTimeMax']}
                                  label={<span style={{ display: 'none' }}></span>}
                                >
                                  <InputNumber<number>
                                    disabled={isFinalStatus}
                                    controls={false}
                                    size={'large'}
                                    onBlur={(e) =>
                                      handleFormItemBlur(
                                        'leadTimeMax',
                                        toNumberOrMinusOne(e.target.value),
                                        _brand.purchaseRequestId
                                      )
                                    }
                                    onBeforeInput={(event: any) => preventNonNumericValue(event)}
                                    style={{ width: '100%' }}
                                    suffix={
                                      <Form.Item
                                        noStyle
                                        name={['brands', index, 'timeUnit']}
                                        label={<span style={{ display: 'none' }}></span>}
                                      >
                                        <Row className={'gap-4'}>
                                          <Tag
                                            className={`tag ${_brand.timeUnit === 'DAY' && 'selected-tag'} ${isFinalStatus ? 'disabled-tag' : ''}`}
                                            onClick={(event) => {
                                              handleFormItemBlur('timeUnit', 'DAY', _brand.purchaseRequestId);
                                              event.stopPropagation();
                                            }}
                                          >
                                            Day
                                          </Tag>
                                          <Tag
                                            className={`tag ${_brand.timeUnit === 'WEEK' && 'selected-tag'} ${isFinalStatus ? 'disabled-tag' : ''}`}
                                            onClick={(event) => {
                                              handleFormItemBlur('timeUnit', 'WEEK', _brand.purchaseRequestId);
                                              event.stopPropagation();
                                            }}
                                          >
                                            Week
                                          </Tag>
                                        </Row>
                                      </Form.Item>
                                    }
                                    placeholder={'Max'}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={6}>
                                <Form.Item
                                  name={['brands', index, 'margin']}
                                  label={'Margin'}
                                  rules={[
                                    {
                                      validator: (_, value) => {
                                        if (value === undefined || value === null || value === '') {
                                          return Promise.resolve();
                                        }
                                        const num = parseFloat(value);
                                        if (isNaN(num)) {
                                          return Promise.reject(new Error('Margin must be a valid number.'));
                                        }
                                        if (num < 0) {
                                          return Promise.reject(new Error('Margin must be a positive number.'));
                                        }
                                        if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                                          return Promise.reject(new Error('Margin can have up to 2 decimal points.'));
                                        }
                                        return Promise.resolve();
                                      },
                                    },
                                  ]}
                                >
                                  <InputNumber<number>
                                    disabled={isFinalStatus}
                                    controls={false}
                                    size={'large'}
                                    onBlur={(e) =>
                                      handleFormItemBlur(
                                        'margin',
                                        toNumberOrMinusOne(e.target.value),
                                        _brand.purchaseRequestId
                                      )
                                    }
                                    onBeforeInput={(event: any) => preventNonNumericValue(event)}
                                    style={{ width: '100%' }}
                                    suffix={<Text type={'secondary'}>%</Text>}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={6}>
                                <Form.Item
                                  name={['brands', index, 'minimumOrderFee']}
                                  label={'Minimum Order Fee'}
                                  rules={[
                                    {
                                      validator: (_, value) => {
                                        if (value === undefined || value === null || value === '') {
                                          return Promise.resolve();
                                        }
                                        const num = parseFloat(value);
                                        if (isNaN(num)) {
                                          return Promise.reject(new Error('Minimum Order Fee must be a valid number.'));
                                        }
                                        if (num < 0) {
                                          return Promise.reject(
                                            new Error('Minimum Order Fee must be a positive number.')
                                          );
                                        }
                                        if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                                          return Promise.reject(
                                            new Error('Minimum Order Fee can have up to 2 decimal points.')
                                          );
                                        }
                                        return Promise.resolve();
                                      },
                                    },
                                  ]}
                                >
                                  <InputNumber<number>
                                    disabled={isFinalStatus}
                                    controls={false}
                                    size={'large'}
                                    onBlur={(e) =>
                                      handleFormItemBlur(
                                        'minimumOrderFee',
                                        toNumberOrMinusOne(e.target.value),
                                        _brand.purchaseRequestId
                                      )
                                    }
                                    onBeforeInput={(event: any) => preventNonNumericValue(event)}
                                    style={{ width: '100%' }}
                                    suffix={<Text type={'secondary'}>€</Text>}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Form>
                        </Col>
                        <Col span={24}>
                          <CommercialOfferItemsTable
                            originalForm={form}
                            commercialOfferId={commercialOfferId}
                            initialItems={_brand.items || []}
                            data={_brand}
                          />
                        </Col>
                        <Col span={24}>
                          <Row>
                            {_brand.substituteAlternativeItems?.map((_item: any) => {
                              return _item.items?.length ? (
                                <Col span={24} key={_item.brandId}>
                                  <CommercialOfferItemsTable
                                    originalForm={form}
                                    commercialOfferId={commercialOfferId}
                                    subTable={true}
                                    data={_brand}
                                    originalItemId={_item.skuOriginalItem}
                                    subTableType={_item.type}
                                    subTableBrand={_item.brandName}
                                    subTableBrandId={_item.brandId}
                                    subTableItemNumber={_item.itemId}
                                    subTableSku={_item.skuOriginalItem}
                                    initialItems={_item.items || []}
                                  />
                                </Col>
                              ) : null;
                            })}
                          </Row>
                        </Col>
                      </Row>
                    ),
                  },
                ]}
              />
            );
          })}
        </Col>
        <Col span={24}>
          <Row gutter={[0, 40]}></Row>
        </Col>
        <Divider className={styles.divider} />
        <Col span={24}>
          <Row justify={'space-between'} align={'middle'}>
            <Col span={6}>
              <Row gutter={[0, 2]}>
                <Col span={24}>
                  <Text className={'font-size-30 font-weight-700 no-wrap'}>
                    Total:
                    {commercialOfferDetails.totalMin === commercialOfferDetails.totalMax
                      ? commercialOfferDetails.totalMin
                      : `${commercialOfferDetails.totalMin} - ${commercialOfferDetails.totalMax}`}
                  </Text>
                </Col>
                <Col>
                  <Text type={'secondary'}>
                    Valid until:
                    {commercialOfferDetails.validityDate
                      ? dayjs(commercialOfferDetails.validityDate).format('YYYY-MM-DD')
                      : ''}
                  </Text>
                </Col>
              </Row>
            </Col>
            <Col>
              <Row justify="end" gutter={6}>
                <Col>
                  <Button
                    disabled={isFinalStatus}
                    className={styles.action_button}
                    block
                    size="large"
                    onClick={navigateToCards}
                  >
                    Save Draft
                  </Button>
                </Col>
                <Col>
                  <Dropdown
                    menu={{
                      items: availableActions,
                      onClick: ({ key }) => handleMenuClick && handleMenuClick({ key }),
                    }}
                    trigger={['click']}
                    disabled={!availableActions?.length}
                  >
                    <Button disabled={isFinalStatus} size={'large'} className={styles.action_button} block>
                      Send <DownOutlined />
                    </Button>
                  </Dropdown>
                </Col>
                <Col>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'generateOffer',
                          label: 'Generate Offer',
                        },
                        {
                          key: 'generatePreInvoice',
                          label: 'Generate Pre-Invoice',
                        },
                        {
                          key: 'generateOrderConfirmation',
                          label: 'Generate Order Confirmation',
                        },
                      ],
                      onClick: ({ key }) => handleMenuClick && handleMenuClick({ key }),
                    }}
                    trigger={['click']}
                  >
                    <Button disabled={isFinalStatus} type="primary" block size={'large'}>
                      Generate <DownOutlined />
                    </Button>
                  </Dropdown>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      <AddressAddEditModal
        onModalStateChange={onAddModalStateChange}
        modalState={addModalState}
        clientId={commercialOfferDetails.clientId}
      />
    </>
  );
});
